import { useEffect, useState } from 'react'
import type { DirectoryUserRow } from '@ai-meeting/shared'
import { fetchDirectoryUsers } from '@/services/directory'

export interface TenantDirectory {
  users: DirectoryUserRow[]
  loading: boolean
}

/**
 * Loads the Microsoft tenant's users for a picker.
 *
 * Split out of the component so any screen that needs to name a person by email
 * gets the list in one line, and so `TenantUserSelect` stays presentational and
 * testable without a network stub.
 *
 * Never surfaces an error: the directory is a convenience, and a tenant that has
 * not granted the Graph permission simply has no suggestions. The caller decides
 * what an empty list means for its own screen.
 *
 * @param enabled Skip the request while the screen is not being shown — admin
 *   tabs mount before they are opened.
 */
export function useTenantDirectory(enabled = true): TenantDirectory {
  const [users, setUsers] = useState<DirectoryUserRow[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!enabled) return undefined

    let cancelled = false
    setLoading(true)
    void fetchDirectoryUsers().then((result) => {
      if (cancelled) return
      setUsers(result.users)
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [enabled])

  return { users, loading }
}
